/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useEffect, useState } from "react";
import {
  firebaseConfig,
  getVapidKey,
  getMessagingInstance,
} from "../services/firebase_service";
import { getToken, onMessage } from "firebase/messaging";

// Create Firebase Context
const FirebaseContext = createContext();

// Firebase Provider Component
export const FirebaseProvider = ({ children }) => {
  const [messaging, setMessaging] = useState(null);
  const [fcmToken, setFcmToken] = useState(null);
  const [notificationPermission, setNotificationPermission] =
    useState("default");
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    const initMessaging = async () => {
      const messagingInstance = await getMessagingInstance();
      if (messagingInstance) {
        setMessaging(messagingInstance);
        setIsSupported(true);
        if (typeof Notification !== "undefined") {
          setNotificationPermission(Notification.permission);
        }
      } else {
        setIsSupported(false);
      }
    };

    initMessaging();
  }, []);

  // Request notification permission and get FCM token
  const requestNotificationPermission = async () => {
    try {
      if (!isSupported || !messaging) {
        throw new Error("Browser does not support notifications");
      }

      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);

      if (permission === "granted") {
        const vapidKey = getVapidKey();

        if (!vapidKey) {
          return null;
        }

        const token = await getToken(messaging, { vapidKey });
        setFcmToken(token);

        return token;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  // Auto-request notification permission if not already set
  useEffect(() => {
    const autoRequestPermission = async () => {
      if (isSupported && messaging) {
        if (notificationPermission === "default") {
          await requestNotificationPermission();
        } else if (notificationPermission === "granted") {
          // Generate token even if permission was already granted
          const vapidKey = getVapidKey();

          if (!vapidKey) {
            return;
          }

          try {
            const token = await getToken(messaging, { vapidKey });
            setFcmToken(token);
          } catch (error) {
            // Token generation failed silently
          }
        }
      }
    };

    if (messaging) {
      autoRequestPermission();
      onMessage(messaging, (payload) => {

        // You can customize how you want to display the notification
        // For foreground messages, Firebase does NOT automatically display a notification.
        // You need to create your own UI/logic to show it.

        // Example: Display a custom toast or an in-app notification
        const notificationTitle = payload.notification.title;
        const notificationBody = payload.notification.body;
        const notificationIcon = payload.notification.icon; // Optional

        // You can use the Notification API to show a local notification
        // This will appear as a system notification even in the foreground
        // if the user has granted permission.
        if (Notification.permission === "granted") {
          new Notification(notificationTitle, {
            body: notificationBody,
            icon: notificationIcon,
            // You can add more options like click_action, image, etc.
            // data: payload.data // Pass custom data if needed
          });
        } else {
          // Or display it within your web app's UI (e.g., a modal, toast, or banner)
          alert(`New Message: ${notificationTitle}\n${notificationBody}`);
        }

        // You can also access custom data sent with the notification
      });
    }
  }, [isSupported, messaging, notificationPermission]);

  // Setup foreground notifications

  // Refresh FCM token if needed
  const refreshToken = async () => {
    if (notificationPermission === "granted" && messaging) {
      return await requestNotificationPermission();
    }
    return null;
  };

  const contextValue = {
    // Firebase config
    firebaseConfig,

    // FCM related
    fcmToken,
    notificationPermission,
    isSupported,
    messaging,

    // Methods
    requestNotificationPermission,
    refreshToken,

    // Helper methods
    getProjectId: () => firebaseConfig.projectId,
    isFirebaseInitialized: () => !!firebaseConfig.projectId,
  };

  return (
    <FirebaseContext.Provider value={contextValue}>
      {children}
    </FirebaseContext.Provider>
  );
};

// Custom hook to use Firebase context
export const useFirebase = () => {
  const context = useContext(FirebaseContext);

  if (!context) {
    throw new Error("useFirebase must be used within a FirebaseProvider");
  }

  return context;
};

// Export context for direct access if needed
export { FirebaseContext };
